import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Props as AsyncProps } from 'react-select/async';
import { OptionTypeBase, Props as SelectProps } from 'react-select';
import { FiAlertTriangle } from 'react-icons/fi';

import { useField } from '@unform/core';

import { Container, OptionLabelContainer, ReactSelect } from './styles';
import Tooltip from '../Tooltip';

interface OptionalLabel {
  value: string;
  label: string;
  allyPlusIcon?: string;
}

interface Props extends SelectProps<OptionTypeBase, boolean> {
  name: string;
  register?: boolean;
  containerStyle?: object;
}

const SelectAllyPlus: React.FC<Props> = ({
  name,
  register = true,
  containerStyle = {},
  ...rest
}) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const { fieldName, defaultValue, error, registerField } = useField(name);

  const formatOptionLabel: React.FC<OptionalLabel> = ({
    label,
    allyPlusIcon,
  }) => (
    <OptionLabelContainer>
      <span>{label}</span>
      {allyPlusIcon && (
        <div>
          <img src={allyPlusIcon} alt="Ally+ Icon" />
        </div>
      )}
    </OptionLabelContainer>
  );

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);

    setIsFilled(!!selectRef.current?.value);
  }, []);

  useEffect(() => {
    if (!register) return;

    registerField({
      name: fieldName,
      ref: selectRef.current,
      getValue: (ref: any) => {
        if (rest.isMulti) {
          if (!ref.state.value) {
            return [];
          }
          return ref.state.value.map((option: OptionTypeBase) => option.value);
        }
        if (!ref.state.value) {
          return '';
        }
        return ref.state.value.value;
      },
    });
  }, [fieldName, register, registerField, rest.isMulti]);

  return (
    <Container
      style={containerStyle}
      isErrored={!!error}
      isFilled={isFilled}
      isFocused={isFocused}
      data-testid="input-container"
    >
      <ReactSelect
        defaultValue={defaultValue}
        ref={selectRef}
        classNamePrefix="react-select"
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        formatOptionLabel={formatOptionLabel}
        {...rest}
      />

      {error && (
        <Tooltip title={error}>
          <FiAlertTriangle color="#c53030" size={20} />
        </Tooltip>
      )}
    </Container>
  );
};

export default SelectAllyPlus;
